import {
  ChevronDownIcon,
  InfoCircleIcon,
  PlusIcon,
  Trash04Icon,
} from '@langchain/untitled-ui-icons';
import { Checkbox } from '@mui/joy';

import { dequal } from 'dequal';
import { produce } from 'immer';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { usePlaygroundStore } from '@/Pages/Playground/PlaygroundHome/hooks/usePlaygroundStore';
import {
  Badge,
  Button,
  IconButton,
  Input,
  Text,
  Tooltip,
} from '@/design-system';
import { useIsPromptLoading } from '@/hooks/useIsPromptLoading';
import { getColorByString } from '@/utils/get-color-by-string';
import { cn } from '@/utils/tailwind';

import { CodeDataEditor } from '../Code/Code';
import { EditInputFunctionParameters } from '../ExampleCrudPane/EditInputFunctions';
import { FeedbackChip } from '../FeedbackChips';
import { useResizeTextEditor } from '../PlainTextEditor/hooks/useResizeTextEditor';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { SchemaEditorVariant } from '../SchemaEditor/types';
import { Skeleton } from '../Skeleton';
import { EvaluatorHubPromptPlaygroundLink } from './HubPromptPlaygroundLink';
import {
  DEFAULT_BOOLEAN_FEEDBACK,
  DEFAULT_CATEGORICAL_FEEDBACK,
  DEFAULT_CONTINUOUS_FEEDBACK,
  FEEDBACK_TYPES,
} from './constants';
import { useEvaluatorStore } from './store/evaluatorStore';
import { EvaluatorFeedbackType } from './types';
import {
  ContinuousMetadata,
  EvaluatedEntityType,
  FeedbackMetadata,
  SchemaDefinition,
  deserializeSchemaToFeedback,
  serializeFeedbackToSchema,
} from './utils/serializeFeedbackUIToLC';
import { useParsedManifest } from './utils/useParsedManifest';
import { validateOutputSchemaKeys } from './utils/validateOutputSchemaKeys';

const SectionHeader = ({
  feedbackType,
  setFeedbackType,
  readOnly,
  rightContent,
}: {
  feedbackType: EvaluatorFeedbackType;
  setFeedbackType: (feedbackType: EvaluatorFeedbackType) => void;
  readOnly?: boolean;
  rightContent?: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);

  const feedbackTypeButton = (
    <Button
      variant="outlined"
      color="secondary"
      size="sm"
      onClick={() => setOpen(true)}
      disabled={readOnly}
      tagText={
        feedbackType === 'categorical'
          ? 'Categorical'
          : feedbackType === 'continuous'
          ? 'Score'
          : 'Boolean'
      }
      rightDecorator={readOnly ? undefined : ChevronDownIcon}
    >
      Response Format
    </Button>
  );

  return (
    <div className="flex items-center justify-between pt-2">
      {readOnly ? (
        feedbackTypeButton
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{feedbackTypeButton}</PopoverTrigger>
          <PopoverContent className="w-40 p-0" align="start">
            <div className="flex flex-col rounded-md py-1">
              {FEEDBACK_TYPES.map(({ type, label }) => (
                <button
                  key={type}
                  type="button"
                  className={cn(
                    'px-3 py-1.5 text-left text-sm hover:bg-secondary',
                    feedbackType === type && 'bg-secondary'
                  )}
                  onClick={() => {
                    setFeedbackType(type as EvaluatorFeedbackType);
                    setOpen(false);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
      {rightContent}
    </div>
  );
};

const FeedbackNameTag = ({
  name,
  onChange,
  readOnly,
}: {
  name: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          'flex items-center gap-2 whitespace-nowrap rounded-md border border-secondary bg-background px-2 py-[3px] text-xs',
          readOnly && 'bg-secondary text-tertiary'
        )}
      >
        <span
          className="h-2 w-2 min-w-2 shrink-0 rounded-sm"
          style={{ backgroundColor: getColorByString(name) }}
        />
        <input
          ref={inputRef}
          disabled={readOnly}
          type="text"
          className="w-[130px] border-none bg-transparent p-0 text-xs outline-none"
          placeholder="Unnamed Feedback"
          value={name}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      </div>
    </div>
  );
};

export function EvaluatorFeedbackSchema(props: {
  evaluatedEntityType: EvaluatedEntityType;
  isAlignEvaluator?: boolean;
  readOnly?: boolean;
  hideDescriptionAndControls?: boolean;
  isEvaluatorPlayground?: boolean;
}) {
  const feedbackEditorType = useEvaluatorStore(
    (state) => state.feedbackEditorType
  );
  const setFeedbackEditorType = useEvaluatorStore(
    (state) => state.setFeedbackEditorType
  );
  const isLoading = useIsPromptLoading(props.evaluatedEntityType);

  const { manifestStructuredSchema } = useParsedManifest(
    props.evaluatedEntityType
  );

  const _setFeedbackSchema = useEvaluatorStore(
    (state) => state.setFeedbackSchema
  );
  const evaluatorType = useEvaluatorStore((state) => state.evaluatorType);
  const evaluatorVersion = useEvaluatorStore((state) => state.evaluatorVersion);
  const readOnly =
    props.readOnly ?? (evaluatorType === 'hub' && evaluatorVersion < 3);

  // Playground store synchronization for evaluator playground
  const setManifest = usePlaygroundStore((state) => state.setManifest);

  const setFeedbackSchema = useCallback(
    (schema: SchemaDefinition) => {
      _setFeedbackSchema(schema);
      if (!props.isEvaluatorPlayground) {
        return;
      }
      // If in the evaluator playground, we need to update the playground store to reflect the new schema as well

      setManifest(0, (prev) => {
        const schemaToSet = prev.kwargs?.first?.schema
          ? {
              schema,
            }
          : {
              schema_: schema,
            };
        return {
          ...prev,
          kwargs: {
            ...prev.kwargs,
            first: {
              ...prev.kwargs.first,
              kwargs: {
                ...prev.kwargs.first.kwargs,
                ...schemaToSet,
              },
            },
          },
        };
      });
    },
    [_setFeedbackSchema, props.isEvaluatorPlayground, setManifest]
  );

  if (isLoading) {
    return <Skeleton className="h-[150px] w-full rounded-lg" />;
  }

  const renderFeedbackEditor = () => {
    if (!manifestStructuredSchema) {
      return null;
    }
    if (feedbackEditorType === 'pretty') {
      return (
        <FeedbackEditor
          readOnly={readOnly}
          allowEditDescriptionAndReasoning={props.isAlignEvaluator}
          feedbackSchema={manifestStructuredSchema}
          setFeedbackSchema={setFeedbackSchema}
        />
      );
    }
    if (feedbackEditorType === 'json') {
      return (
        <div className="relative rounded-md border border-secondary">
          <Button
            onClick={() => setFeedbackEditorType('pretty')}
            variant="outlined"
            color="secondary"
            className="absolute right-4 top-4 z-10"
          >
            Editor
          </Button>

          <CodeDataEditor
            value={manifestStructuredSchema}
            onChange={(value) => {
              if (dequal(value, manifestStructuredSchema)) {
                return;
              }
              setFeedbackSchema(value as SchemaDefinition);
            }}
            language="json"
            readOnly={readOnly}
          />
        </div>
      );
    }
    if (feedbackEditorType === 'schema') {
      return (
        <div className="relative rounded-md border border-secondary">
          <Button
            onClick={() => setFeedbackEditorType('json')}
            variant="outlined"
            color="secondary"
            className="absolute right-4 top-4 z-10"
          >
            Advanced
          </Button>

          <EditInputFunctionParameters
            variant={SchemaEditorVariant.EVALUATOR}
            value={manifestStructuredSchema}
            onChange={(value) => setFeedbackSchema(value as SchemaDefinition)}
            hideTitle={!manifestStructuredSchema?.title}
            readOnly={readOnly}
            showBorder={true}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-1">
      {!props.hideDescriptionAndControls && (
        <>
          <label className="flex items-center gap-1 font-semibold">
            Feedback configuration{' '}
          </label>
          {evaluatorType === 'hub' && evaluatorVersion < 3 ? (
            <div className="mb-3 text-sm text-secondary">
              To edit feedback configuration, go to the{' '}
              <EvaluatorHubPromptPlaygroundLink />
            </div>
          ) : (
            <div className="mb-3 text-sm text-secondary">
              Define your evaluation criteria. Describe what you're measuring,
              then select a response format. This configuration structures how
              your evaluation results are returned.
            </div>
          )}
        </>
      )}
      {props.isAlignEvaluator && !props.isEvaluatorPlayground && (
        <div className="mb-2 flex items-center rounded-md bg-brand-tertiary px-3 py-2.5 text-xs">
          <InfoCircleIcon className="mr-1 h-3 w-3 text-brand-primary" />
          <Text className="text-brand-primary" variant="xs" weight="medium">
            Editing output schema for an alignment evaluator is not supported.
          </Text>
        </div>
      )}

      {renderFeedbackEditor()}
    </div>
  );
}

const CATEGORICAL_FALLBACK_TEXT = 'No description provided for category';

// Categorical Feedback Editor Component
const CategoricalFeedbackEditor = ({
  readOnly,
  metadata,
  setMetadata,
  addCategory,
}: {
  metadata: Extract<FeedbackMetadata, { type: 'categorical' }>;
  setMetadata: (metadata: FeedbackMetadata) => void;
  readOnly?: boolean;
  addCategory: () => void;
}) => {
  const removeCategory = (index: number) => {
    setMetadata(
      produce(metadata, (draft) => {
        draft.categoricalConfig.categories.splice(index, 1);
      })
    );
  };

  const updateCategory = (
    index: number,
    field: 'category' | 'description',
    value: string
  ) => {
    setMetadata(
      produce(metadata, (draft) => {
        draft.categoricalConfig.categories[index][field] = value;
      })
    );
  };

  return (
    <div className="flex flex-col items-stretch gap-2 pt-3">
      {!readOnly && (
        <div className="flex items-center gap-2 text-sm font-medium text-secondary">
          <div className="w-[200px] flex-none">Category</div>
          <div className="flex-1">Description</div>
          <div className="w-[40px] flex-none"></div>
        </div>
      )}

      {metadata.categoricalConfig.categories.length === 0 && (
        <div className="pb-4 text-sm text-secondary">
          {'No categories defined.' +
            (readOnly ? '' : ' Add a category to get started.')}
        </div>
      )}

      {metadata.categoricalConfig.categories.map((category, index) =>
        readOnly ? (
          <div key={index} className="flex w-full rounded-md">
            {category.category && (
              <div
                className={cn(
                  'rounded-md border border-secondary bg-secondary px-1 py-1 text-sm text-secondary',
                  readOnly && 'bg-primary px-2'
                )}
              >
                {category.category}
              </div>
            )}
            <div className="px-2 py-1 text-sm text-secondary">
              {category.description || (
                <span className="text-tertiary">
                  {CATEGORICAL_FALLBACK_TEXT}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div
              key={index}
              className="flex w-full items-center gap-2 rounded-md"
            >
              <div className="w-[200px] flex-none">
                <Input
                  size="md"
                  onChange={(e) => updateCategory(index, 'category', e)}
                  value={category.category}
                />
              </div>
              <div className="flex-1">
                <Input
                  size="md"
                  onChange={(e) => updateCategory(index, 'description', e)}
                  value={category.description}
                />
              </div>
              <div className="flex w-[40px] flex-none justify-center">
                <IconButton
                  size="md"
                  variant="outlined"
                  color="secondary"
                  icon={Trash04Icon}
                  label="Remove category"
                  onClick={() => removeCategory(index)}
                  className="p-2.5"
                />
              </div>
            </div>
          </div>
        )
      )}
      <div className="pt-2">
        <Button
          size="md"
          variant="underlined"
          color="secondary"
          onClick={addCategory}
          leftDecorator={PlusIcon}
        >
          Category
        </Button>
      </div>
    </div>
  );
};

// Continuous Feedback Component
const CategoricalFeedbackDisplay = ({
  metadata,
}: {
  metadata: Extract<FeedbackMetadata, { type: 'categorical' }>;
}) => {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-2 pt-3">
      {metadata.categoricalConfig.categories.map((category, index) => (
        <>
          <div
            key={index}
            className="flex-0 flex items-center justify-between gap-2 self-start rounded-lg bg-tertiary p-3"
          >
            <Text variant="body" className="font-medium leading-none">
              {category.category}
            </Text>
          </div>
          <div
            key={`${index}-description`}
            className="flex flex-1 rounded-lg bg-tertiary p-2 px-3"
          >
            <Text
              variant="body"
              className={cn(!category.description && 'text-disabled')}
            >
              {category.description || CATEGORICAL_FALLBACK_TEXT}
            </Text>
          </div>
        </>
      ))}
    </div>
  );
};

// Continuous Feedback Editor Component
const ContinuousFeedbackEditor = ({
  readOnly,
  metadata,
  setMetadata,
}: {
  metadata: Extract<FeedbackMetadata, { type: 'continuous' }>;
  setMetadata: (metadata: FeedbackMetadata) => void;
  readOnly?: boolean;
}) => {
  // Update continuous feedback settings
  const updateContinuous = (
    field: keyof ContinuousMetadata,
    value: string | number
  ) => {
    // Convert string values to numbers for min/max fields
    const processedValue =
      field === 'min' || field === 'max'
        ? typeof value === 'string'
          ? Number(value)
          : value
        : value;

    setMetadata({
      ...metadata,
      continuousConfig: {
        ...metadata.continuousConfig,
        [field]: processedValue,
      },
    });
  };

  return (
    <div className="flex flex-col items-stretch gap-2 pt-3">
      {/* Min Section */}
      <div className="flex gap-2">
        <div className="flex flex-col gap-2">
          <Text variant="sm" className="font-medium text-secondary">
            Min
          </Text>
          <Input
            type="number"
            value={metadata.continuousConfig.min.toString()}
            onChange={(e) => updateContinuous('min', Number(e))}
            disabled={readOnly}
          />
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <Text variant="sm" className="font-medium text-secondary">
            Description for minimum value
          </Text>
          <Input
            type="text"
            value={
              readOnly
                ? metadata.continuousConfig.minDescription ||
                  'No description provided'
                : metadata.continuousConfig.minDescription
            }
            onChange={(e) => updateContinuous('minDescription', e)}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Max Section */}
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <div className="flex flex-col gap-2">
            <Text variant="sm" className="font-medium text-secondary">
              Max
            </Text>
            <Input
              type="number"
              value={metadata.continuousConfig.max.toString()}
              onChange={(e) => updateContinuous('max', Number(e))}
              disabled={readOnly}
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <Text variant="sm" className="font-medium text-secondary">
              Description for maximum value
            </Text>
            <Input
              type="text"
              value={
                readOnly
                  ? metadata.continuousConfig.maxDescription ||
                    'No description provided'
                  : metadata.continuousConfig.maxDescription
              }
              onChange={(e) => updateContinuous('maxDescription', e)}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Continuous Feedback Component
const ContinuousFeedbackDisplay = ({
  metadata,
}: {
  metadata: Extract<FeedbackMetadata, { type: 'continuous' }>;
}) => {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-2 pt-3">
      {/* Min Section */}
      <div className="flex-0 flex items-center justify-between gap-2 self-start rounded-lg bg-tertiary p-2 px-3">
        <Text variant="sm" className="font-medium leading-none text-tertiary">
          Min
        </Text>
        <Text variant="body" className="font-medium">
          {metadata.continuousConfig.min}
        </Text>
      </div>
      <div className="flex flex-1 rounded-lg bg-tertiary p-2 px-3">
        <Text
          variant="body"
          className={cn(
            !metadata.continuousConfig.minDescription && 'text-disabled'
          )}
        >
          {metadata.continuousConfig.minDescription ||
            'No description provided'}
        </Text>
      </div>

      {/* Max Section */}
      <div className="flex-0 flex items-center justify-between gap-2 self-start rounded-lg bg-tertiary p-2 px-3">
        <Text variant="sm" className="font-medium leading-none text-tertiary">
          Max
        </Text>
        <Text variant="body" className="font-medium">
          {metadata.continuousConfig.max}
        </Text>
      </div>
      <div className="flex flex-1 rounded-lg bg-tertiary p-2 px-3">
        <Text
          variant="body"
          className={cn(
            !metadata.continuousConfig.maxDescription && 'text-disabled'
          )}
        >
          {metadata.continuousConfig.maxDescription ||
            'No description provided'}
        </Text>
      </div>
    </div>
  );
};

const FeedbackEditor = ({
  readOnly,
  allowEditDescriptionAndReasoning,
  setFeedbackSchema,
  feedbackSchema,
}: {
  readOnly?: boolean;
  allowEditDescriptionAndReasoning?: boolean;
  setFeedbackSchema: (schema: SchemaDefinition) => void;
  feedbackSchema: SchemaDefinition;
}) => {
  const feedbackEditorType = useEvaluatorStore(
    (state) => state.feedbackEditorType
  );
  const setFeedbackEditorType = useEvaluatorStore(
    (state) => state.setFeedbackEditorType
  );

  const defaultFeedback = deserializeSchemaToFeedback(feedbackSchema);
  const [name, setName] = useState<string>(defaultFeedback?.name ?? '');
  const [description, setDescription] = useState<string>(
    defaultFeedback?.description ?? ''
  );
  const [metadata, setMetadata] = useState<FeedbackMetadata>(
    defaultFeedback?.metadata ?? DEFAULT_BOOLEAN_FEEDBACK
  );

  useEffect(() => {
    setName(defaultFeedback?.name ?? '');
    setDescription(defaultFeedback?.description ?? '');
    setMetadata(defaultFeedback?.metadata ?? DEFAULT_BOOLEAN_FEEDBACK);
  }, [feedbackSchema]);

  const [includeReasoning, setIncludeReasoning] = useState<boolean>(
    defaultFeedback?.includeReasoning ?? false
  );

  const setSerializedSchema = (
    name: string,
    description: string,
    metadata: FeedbackMetadata,
    includeReasoning: boolean
  ) => {
    setFeedbackSchema(
      serializeFeedbackToSchema(name, description, metadata, includeReasoning)
    );
  };

  const handleNameChange = (name: string) => {
    setName(name);
    if (name !== 'comment') {
      setSerializedSchema(name, description, metadata, includeReasoning);
    }
  };

  const handleDescriptionChange = (description: string) => {
    setDescription(description);
    setSerializedSchema(name, description, metadata, includeReasoning);
  };

  const handleMetadataChange = (newMetadata: FeedbackMetadata) => {
    setMetadata(newMetadata);
    setSerializedSchema(name, description, newMetadata, includeReasoning);
  };

  const handleFeedbackTypeChange = (type: EvaluatorFeedbackType) => {
    if (type === 'categorical') {
      handleMetadataChange(DEFAULT_CATEGORICAL_FEEDBACK);
    } else if (type === 'continuous') {
      handleMetadataChange(DEFAULT_CONTINUOUS_FEEDBACK);
    } else if (type === 'boolean') {
      handleMetadataChange(DEFAULT_BOOLEAN_FEEDBACK);
    }
  };

  const handleIncludeReasoningChange = (includeReasoning: boolean) => {
    setIncludeReasoning(includeReasoning);
    setSerializedSchema(name, description, metadata, includeReasoning);
  };

  const addCategory = () => {
    if (metadata.type === 'categorical') {
      setMetadata(
        produce(metadata, (draft) => {
          draft.categoricalConfig.categories.push({
            category: `category`,
            description: '',
          });
        })
      );
    }
  };

  const { textAreaRef } = useResizeTextEditor({
    internalValue: description,
    maxHeight: '400px',
  });

  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border border-secondary p-4',
        readOnly && 'border-none bg-secondary'
      )}
    >
      <div className="flex flex-col pb-3">
        <div className="flex w-full justify-between">
          <div className="flex">
            {readOnly ? (
              <FeedbackChip feedback_key={name} />
            ) : (
              <FeedbackNameTag name={name} onChange={handleNameChange} />
            )}
          </div>

          <div className="flex items-center justify-end gap-4">
            <div className="flex items-center gap-1.5">
              {readOnly && !allowEditDescriptionAndReasoning ? (
                <>
                  <div className="text-xs text-tertiary">
                    {includeReasoning
                      ? 'Reasoning included'
                      : 'Reasoning not included'}
                  </div>
                  <Tooltip
                    title={
                      includeReasoning
                        ? 'This evaluator will output reasoning along with a score'
                        : 'This evaluator will not output reasoning along with a score'
                    }
                  >
                    <div className="text-secondary">
                      <InfoCircleIcon className="h-4 w-4" />
                    </div>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Checkbox
                    disabled={name === 'comment'}
                    checked={includeReasoning}
                    onChange={(e) =>
                      handleIncludeReasoningChange(e.target.checked)
                    }
                    label={
                      <div className="text-secondary">Include reasoning</div>
                    }
                    size="sm"
                  />

                  <Tooltip
                    title={
                      'When checked, evaluator will provide a detailed explanation of how it arrived at this score'
                    }
                  >
                    <div className="text-secondary">
                      <InfoCircleIcon className="h-4 w-4" />
                    </div>
                  </Tooltip>
                </>
              )}
            </div>
            {feedbackEditorType !== 'schema' && !readOnly && (
              <Button
                onClick={() => setFeedbackEditorType('json')}
                variant="outlined"
                color="secondary"
              >
                Advanced
              </Button>
            )}
          </div>
        </div>
        {name === 'comment' && (
          <span className="mt-1 text-xs text-error">
            Feedback name cannot be reserved keyword "comment"
          </span>
        )}
        {name &&
          name !== 'comment' &&
          !validateOutputSchemaKeys([name]).isValid && (
            <span className="mt-1 text-xs text-error">
              Feedback name cannot contain whitespace or capital letters
            </span>
          )}
      </div>

      {/* Basic Information */}
      {!readOnly && (
        <Text variant="xs" className="pb-2 text-secondary">
          Description
        </Text>
      )}
      {readOnly && !allowEditDescriptionAndReasoning ? (
        <Text variant="body" className="mb-2 rounded-md">
          {description || (
            <span className="text-tertiary">No description provided</span>
          )}
        </Text>
      ) : (
        <textarea
          ref={textAreaRef}
          disabled={readOnly && !allowEditDescriptionAndReasoning}
          className={cn(
            'w-full resize-none overflow-hidden border border-primary bg-primary px-3 py-2',
            'rounded-md text-xs font-normal leading-[18px] outline-none focus:border-brand',
            ' disabled:bg-secondary disabled:text-tertiary',
            !readOnly && 'mb-2'
          )}
          placeholder="Feedback description"
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
        />
      )}

      {!readOnly && (
        <SectionHeader
          readOnly={readOnly}
          feedbackType={metadata.type}
          setFeedbackType={handleFeedbackTypeChange}
        />
      )}

      {/* Render appropriate editor based on feedback type */}
      {metadata.type === 'categorical' &&
        (readOnly ? (
          <CategoricalFeedbackDisplay metadata={metadata} />
        ) : (
          <CategoricalFeedbackEditor
            readOnly={readOnly}
            metadata={metadata}
            setMetadata={handleMetadataChange}
            addCategory={addCategory}
          />
        ))}

      {metadata.type === 'continuous' &&
        (readOnly ? (
          <ContinuousFeedbackDisplay metadata={metadata} />
        ) : (
          <ContinuousFeedbackEditor
            readOnly={readOnly}
            metadata={metadata}
            setMetadata={handleMetadataChange}
          />
        ))}

      {metadata.type === 'boolean' && (
        <p className="mt-2 flex gap-2 rounded-md bg-tertiary px-3 py-2 text-xs text-secondary">
          {readOnly && (
            <Badge
              color="secondary"
              className="bg-quaternary"
              size="xs"
              rounded="xs"
            >
              Boolean
            </Badge>
          )}
          <span>
            The evaluator will provide a <strong>true (1)</strong> or{' '}
            <strong>false (0)</strong> response based on the feedback criteria.
          </span>
        </p>
      )}
    </div>
  );
};
